import {
  AfterViewInit,
  Component,
  ComponentRef,
  ErrorHandler,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  reflectComponentType,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { LazyLoad, LazyLoadComponentConfig } from '@se-po/shared-util'

export type LazyLoadComponentOutputs<T extends Record<string, unknown> = Record<string, unknown>> = Partial<{
  [K in keyof T]: (event: T[K]) => void
}>

interface LazyLoadOutputRef<T = unknown> {
  subscribe(callback: (event: T) => void): { unsubscribe(): void }
}

const CONTENT_READY_OUTPUT = 'contentReady'

@Component({
  selector: 'se-po-lazy-load',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div [hidden]="!showLoading || !componentConfig?.skeleton">
      <ng-container #skeletonContent></ng-container>
    </div>
    <div [hidden]="!showLoading || componentConfig?.skeleton">loading</div>
    <div [hidden]="showLoading">
      <ng-container #componentContent></ng-container>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0 ;
    }
  `]
})
export class LazyLoadComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('componentContent', { read: ViewContainerRef })
  private componentContent!: ViewContainerRef
  @ViewChild('skeletonContent', { read: ViewContainerRef })
  private skeletonContent!: ViewContainerRef

  @Input() public componentConfig: LazyLoadComponentConfig | null = null
  @Input() public componentInputs: Record<string, unknown> = {}
  @Input() public componentOutputs: LazyLoadComponentOutputs = {}
  @Input() public groupLoaded = true
  @Input() public hostReady = true
  @Input() public active = true
  @Input() public loadKey: unknown
  @Output() public loadingChange = new EventEmitter<boolean>()
  @Output() public loadingError = new EventEmitter<void>()

  public loading = true

  private componentRef?: ComponentRef<any>
  private skeletonRef?: ComponentRef<unknown>
  private componentOutputSubscriptions: Array<{ unsubscribe(): void }> = []
  private contentReadySubscription?: { unsubscribe(): void }
  private hasViewInitialized = false
  private loadInProgress = false
  private loadId = 0
  private injector = inject(Injector)
  private errorHandler = inject(ErrorHandler)

  public get showLoading(): boolean {
    return !this.hostReady || this.loading || !this.groupLoaded
  }

  public ngAfterViewInit(): void {
    this.hasViewInitialized = true
    this.renderSkeleton()
    this.loadWhenReady()
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasViewInitialized) return
    if (changes.componentConfig || changes.loadKey) {
      this.resetComponent()
      this.renderSkeleton()
      this.loadWhenReady()
      return
    }
    if (changes.componentInputs) this.setComponentInputs()
    if (changes.componentOutputs) this.setComponentOutputs()
    if (changes.active) this.loadWhenReady()
  }

  public ngOnDestroy(): void {
    this.loadId += 1
    this.loadInProgress = false
    this.unsubscribeFromComponentOutputs()
    this.unsubscribeFromContentReady()
    this.componentRef?.destroy()
    this.skeletonRef?.destroy()
    this.componentContent?.clear()
    this.skeletonContent?.clear()
  }

  private async loadComponent(): Promise<void> {
    if (!this.componentConfig || !this.active || this.componentRef || this.loadInProgress) return

    const currentLoadId = ++this.loadId
    this.loadInProgress = true
    this.setLoading(true)
    this.componentContent.clear()

    let componentRef: ComponentRef<any> | undefined
    try {
      componentRef = await LazyLoad.component(
        this.componentConfig,
        this.componentContent,
        this.injector,
        () => currentLoadId === this.loadId
      )
    } catch (error) {
      if (currentLoadId === this.loadId) {
        this.loadInProgress = false
        this.loadingError.emit()
        this.errorHandler.handleError(error)
      }
      return
    }

    if (!componentRef) return
    if (currentLoadId !== this.loadId) {
      componentRef.destroy()
      return
    }

    this.loadInProgress = false
    this.componentRef = componentRef
    const waitsForContentReady = this.setContentReadyOutput()
    this.setComponentInputs()
    this.setComponentOutputs()
    if (!waitsForContentReady) this.finishLoading()
  }

  private loadWhenReady(): void {
    if (!this.hasViewInitialized || !this.active) return
    void this.loadComponent()
  }

  private resetComponent(): void {
    this.loadId += 1
    this.loadInProgress = false
    this.setLoading(true)
    this.unsubscribeFromComponentOutputs()
    this.unsubscribeFromContentReady()
    this.componentRef?.destroy()
    this.componentRef = undefined
    this.componentContent.clear()
  }

  private renderSkeleton(): void {
    this.skeletonRef?.destroy()
    this.skeletonContent.clear()
    this.skeletonRef = undefined

    const skeleton = this.componentConfig?.skeleton
    if (!skeleton) return

    this.skeletonRef = this.skeletonContent.createComponent(skeleton, {
      injector: this.injector,
    })
    this.skeletonRef.changeDetectorRef.detectChanges()
  }

  private setLoading(loading: boolean): void {
    if (this.loading === loading) return

    this.loading = loading
    this.loadingChange.emit(loading)
  }

  private setComponentInputs(): void {
    if (!this.componentRef) return

    const acceptedInputs = this.getAcceptedInputs(this.componentRef)
    Object.entries(this.componentInputs).forEach(([name, value]) => {
      if (acceptedInputs.has(name)) this.componentRef?.setInput(name, value)
    })
    this.componentRef.changeDetectorRef.detectChanges()
  }

  private getAcceptedInputs(componentRef: ComponentRef<any>): Set<string> {
    const mirror = reflectComponentType(componentRef.componentType)

    return new Set(
      mirror?.inputs.flatMap(input => [input.propName, input.templateName]) ?? []
    )
  }

  private setComponentOutputs(): void {
    this.unsubscribeFromComponentOutputs()
    if (!this.componentRef) return

    const acceptedOutputs = this.getAcceptedOutputs(this.componentRef)
    Object.entries(this.componentOutputs).forEach(([name, callback]) => {
      const outputName = acceptedOutputs.get(name)
      const output = outputName
        ? this.componentRef?.instance[outputName] as LazyLoadOutputRef | undefined
        : undefined

      if (!output || typeof callback !== 'function' || typeof output.subscribe !== 'function') return

      this.componentOutputSubscriptions.push(
        output.subscribe(callback as (event: unknown) => void)
      )
    })
  }

  private setContentReadyOutput(): boolean {
    if (!this.componentConfig?.waitForContentReady || !this.componentRef) return false

    const outputName = this.getAcceptedOutputs(this.componentRef).get(CONTENT_READY_OUTPUT)
    const output = outputName
      ? this.componentRef.instance[outputName] as LazyLoadOutputRef<boolean> | undefined
      : undefined

    if (!output || typeof output.subscribe !== 'function') return false

    this.contentReadySubscription = output.subscribe(ready => {
      if (ready) {
        this.finishLoading()
      } else {
        this.setLoading(true)
        this.renderSkeleton()
      }
    })
    return true
  }

  private finishLoading(): void {
    this.setLoading(false)
  }

  private unsubscribeFromComponentOutputs(): void {
    this.componentOutputSubscriptions.forEach(subscription => subscription.unsubscribe())
    this.componentOutputSubscriptions = []
  }

  private unsubscribeFromContentReady(): void {
    this.contentReadySubscription?.unsubscribe()
    this.contentReadySubscription = undefined
  }

  private getAcceptedOutputs(componentRef: ComponentRef<any>): Map<string, string> {
    const mirror = reflectComponentType(componentRef.componentType)

    return new Map(
      mirror?.outputs.flatMap(output => [
        [output.propName, output.propName],
        [output.templateName, output.propName],
      ]) ?? []
    )
  }
}
